import { GetServerSideProps } from "next";
import { handleRequestError } from "~/utils/rollbar";
import { defaultMediaFields, findStoreBySlug } from "~/strapi/api";
import { getProductAttributes } from "~/api/productAttributes";
import { StrapiProduct, StrapiProductAttribute, StrapiResponse, StrapiStore } from "~/shared-types";
import { SectionHeading } from "~/components/SectionHeading";
import { Breadcrumbs } from "~/components/Breadcrumbs";
import { getRoute } from "~/utils/getRoute";
import {
  GetProductsOptions,
  getProducts,
  useProductsQuery,
} from "~/api/products";
import { TagsMasonry } from "~/components/TagsMasonry";
import { useEffect, useState } from "react";
import Head from "next/head";
import Link from "next/link";
import { useSingleStoreAtom } from "~/atoms/singleStore";
import { useRouter } from "next/router";
import { HTMLLevels } from "~/components/Heading";
import { FeaturedMedia } from "./FeaturedMedia";
import { StoreAddress } from "~/pages/products/[productId]/StoreAddress";
import { StoreContact } from "~/pages/products/[productId]/StoreContact";
import { StoreDescription } from "~/pages/products/[productId]/StoreDescription";
import { EditStoreButton } from "~/components/Backroom/EditStoreButton";
import classNames from "classnames";
import { StoreSocialLinks } from "~/components/StoreSocialLinks";
import { useWithFetchAll } from "~/api/useWithFetchAll";
import { StoreInfoCard } from "../StoreInfoCard";

export type ServerSideProps = {
  store: StrapiStore | null;
  productsPage: StrapiResponse<StrapiProduct[]>;
  productsOptions: GetProductsOptions;
  productAttributes: { id: number; name: string; values: string[] }[];
};

type Props = ServerSideProps & {
  isBackroom?: boolean;
};

export const getProductsOptions = (storeId: string | number) => ({
  populate: [
    "images",
    "subCategories",
    "subCategories.mainCategory",
    "mainCategory",
    "productTags",
    "filterValues",
  ],
  filters: { store: { id: { $eqi: storeId } } },
  sortKey: "order",
  sortDirection: "asc" as const,
});

export const getServerSideProps: GetServerSideProps<
  ServerSideProps,
  { slug: string }
> = async (context) => {
  const { slug } = context.params ?? {};

  if (!slug) return { notFound: true };

  const store = await findStoreBySlug(slug, {
    populate: {
      featuredMedia: { fields: [...(defaultMediaFields || []), "mime"] },
      coverVideo: { fields: [...(defaultMediaFields || []), "mime"] },
      storeImage: { fields: defaultMediaFields },
    },
  }).catch(handleRequestError(context.req, null));

  if (!store) return { notFound: true };
  const productsPage = await getProducts(getProductsOptions(store.id));

    const attrData = await getProductAttributes({}).catch(() => null);
    const productAttributes = (attrData?.data ?? []).map((a) => ({ id: (a as any).id, name: (a as any).attributes.name, values: (a as any).attributes.values ?? [] }));
    return {
      props: { store, productsPage, productsOptions: getProductsOptions(store.id), productAttributes },
  };
};

const StorePage = ({
  productsOptions,
  store,
  productsPage,
  isBackroom,
  productAttributes = [],
}: Props) => {
  if (!store) return null;

  const { name, featuredMedia, coverVideo } = store.attributes;
  const { setSingleStore, onVisitDifferentStore } = useSingleStoreAtom();
  const { query } = useRouter();

  const { data, fetchAllPages } = useWithFetchAll(
    useProductsQuery(productsOptions, productsPage)
  );

  const products = data.pages?.flatMap((page) => page?.data) as StrapiProduct[];
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({});
  const toggleFilter = (attrId: string, value: string) => {
    setActiveFilters(prev => {
      const current = prev[attrId] ?? [];
      const updated = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [attrId]: updated };
    });
  };
  const hasActiveFilters = Object.values(activeFilters).some(v => v.length > 0);
  const filteredProducts = hasActiveFilters
    ? products?.filter(product => {
        const fv = (product.attributes as any).filterValues ?? {};
        return Object.entries(activeFilters).every(([attrId, values]) => {
          if (values.length === 0) return true;
          const productValues = fv[attrId] ?? [];
          return values.some(v => productValues.includes(v));
        });
      })
    : products;

  // Effect: Fetch all pages immediately
  useEffect(() => {
    fetchAllPages();
  }, []);

  // Effect: Set single store if query param is present
  useEffect(() => {
    if (!query.ssv) return onVisitDifferentStore(store.id);

    setSingleStore({ currentStoreId: store.id, limitedStoreId: store.id });
  }, [query.ssv]);

  return (
    <div className="mb-8 md:mb-0">
      <Head>
        <link rel="canonical" href={`https://butikdanmark.dk/producenter/${store.attributes.slug}`} />
        <title>{store.attributes.name} | Butik Danmark</title>
        <meta name="description" content={`Køb produkter fra ${store.attributes.name} på Butik Danmark.`} />
      </Head>
      {!isBackroom && (
        <Breadcrumbs
          links={[
            { href: getRoute("/producenter"), label: "Producenter" },
            { label: name },
          ]}
        />
      )}

      <section className="mt-8 sm:mt-14">
        <div
          className={classNames(
            isBackroom && "flex flex-col md:flex-row md:justify-between"
          )}
        >
          <SectionHeading>
            <SectionHeading.Title level={HTMLLevels.h1}>
              {name}
            </SectionHeading.Title>
          </SectionHeading>
          {isBackroom && (
            <EditStoreButton className="mb-6 order-[-1] md:order-1" />
          )}
        </div>

        <FeaturedMedia
          className="mt-4 sm:mt-8"
          media={coverVideo?.data ?? featuredMedia?.data}
        />

        <div className="flex flex-col gap-20 lg:gap-10 lg:flex-row lg:pr-10">
          <StoreDescription className="pt-8 pb-5" store={store} />
          <StoreInfoCard
            className="relative -mt-10 mx-auto lg:mr-0 hidden lg:block"
            store={store}
          />
        </div>
      </section>

      {products && products.length > 0 && (
        <div className="bg-white mt-4 pt-8 px-[var(--page-x)] md:px-[var(--page-x-md)] -mx-[var(--page-x)] md:-mx-[var(--page-x-md)]">
          <SectionHeading>
            <SectionHeading.Title className="mb-8">
              Produkter
            </SectionHeading.Title>
            <SectionHeading.Subtitle className="mb-10 sm:mb-20">
              Produkter fra{" "}
              <Link
                href={getRoute("/producenter/:slug", { slug: store.attributes?.slug || store.id })}
                className="text-primary"
              >
                {name}
              </Link>
            </SectionHeading.Subtitle>
          </SectionHeading>
          {productAttributes.length > 0 && (
            <div className="flex flex-wrap gap-6 mb-8">
              {productAttributes.map(attr => {
                const attrId = String(attr.id);
                const usedValues = attr.values.filter(v =>
                  products?.some(p => ((p.attributes as any).filterValues?.[attrId] ?? []).includes(v))
                );
                if (usedValues.length === 0) return null;
                return (
                  <div key={attr.id} className="flex flex-col gap-2">
                    <span className="text-sm font-medium text-secondary-600">{attr.name}</span>
                    <div className="flex gap-2 flex-wrap">
                      {usedValues.map(v => {
                        const isActive = (activeFilters[attrId] ?? []).includes(v);
                        return (
                          <button
                            key={v}
                            onClick={() => toggleFilter(attrId, v)}
                            className={`px-3 py-1 rounded-full text-sm border transition ${isActive ? "bg-primary-500 text-white border-primary-500" : "bg-white text-secondary-700 border-secondary-200 hover:border-primary-500"}`}
                          >
                            {v}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <TagsMasonry products={filteredProducts} />
        </div>
      )}

      <StoreInfoCard
        className="relative mx-auto mt-10 -mb-3 md:-mb-10 lg:mr-0 lg:hidden"
        store={store}
      />
    </div>
  );
};

export default StorePage;
